import moment from "moment";

//! @ngInject
export function scheduledStandardReportModalCtrl(
    $scope,
    $rootScope,
    $filter,
    $uibModalInstance,
    reportService,
    toaster
) {
    const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    const WEEKDAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

    $scope.weekdays = WEEKDAYS;
    $scope.loading = true;
    $scope.saving = false;
    $scope.report = null;
    $scope.schedules = [];
    $scope.recipients = [];
    // Held on an object (not a bare primitive) so the ng-if child scope in the
    // template writes through to this controller scope (AngularJS dot rule).
    $scope.recipientInput = { email: "" };
    $scope.schedule = {
        periodType: "WEEKLY",
        dayOfWeek: "MONDAY",
        dayOfMonth: 1,
        // AngularJS input[type=time] binds to a Date; only the time portion is used.
        time: new Date(1970, 0, 1, 9, 0, 0, 0)
    };

    $scope.closeModal = () => $uibModalInstance.dismiss();

    $scope.addRecipient = () => {
        const email = ($scope.recipientInput.email || "").trim();
        if (EMAIL_RE.test(email) && $scope.recipients.indexOf(email) === -1) {
            $scope.recipients.push(email);
            $scope.recipientInput.email = "";
        }
    };

    $scope.removeRecipient = (email) => {
        $scope.recipients = $scope.recipients.filter((e) => e !== email);
    };

    $scope.canSubmit = () => $scope.recipients.length > 0 && !$scope.saving;

    const DATE_FILTER_TYPES = ["DATE", "TIMESTAMP", "TIMESTAMPTZ"];
    const isDateFilter = (filter) => DATE_FILTER_TYPES.indexOf(filter.type) !== -1;
    $scope.isDateFilter = isDateFilter;

    // Toggling "Relative" on a date filter marks it selected so it is included even
    // though the absolute date editor (which normally sets filterSelected) is hidden.
    $scope.onRelativeToggle = (filter) => {
        if (filter.$relative && filter.$relative.enabled) {
            filter.filterSelected = true;
        }
    };

    $scope.handleSchedule = () => {
        const currentReport = angular.copy($scope.report);

        const reportTemplate = {
            ...currentReport,
            filters: parseFilters(currentReport.filters)
        };
        if (currentReport.reportType === "REPORTING_TOOL") {
            reportTemplate.primaryTable = { tableName: currentReport.primaryTable, columns: [] };
        } else {
            // Custom reports (CustomReportRequestBase) have no primaryTable.
            delete reportTemplate.primaryTable;
        }
        // The schedule worker emits CSV; do not pin a "format" on the stored template.
        delete reportTemplate.format;

        const hour = $scope.schedule.time.getHours();
        const minute = $scope.schedule.time.getMinutes();
        const timezone = moment.tz.guess(true);
        const timeInDayDate = moment
            .tz(timezone)
            .hour(hour)
            .minute(minute)
            .second(0)
            .millisecond(0)
            .toISOString();

        const params = {
            periodType: $scope.schedule.periodType,
            timeInDayDate: timeInDayDate,
            interval: $scope.schedule.periodType === "DAILY" ? null : 1,
            dayOfWeek: $scope.schedule.periodType === "WEEKLY" ? $scope.schedule.dayOfWeek : null,
            dayOfMonth: $scope.schedule.periodType === "MONTHLY" ? $scope.schedule.dayOfMonth : null,
            timezone: timezone,
            recipientEmails: $scope.recipients,
            reportTemplate: reportTemplate
        };

        $scope.saving = true;
        reportService
            .addScheduledStandardReport($scope.standardReportId, params)
            .then(() => {
                toaster.pop("success", "Success", "Report scheduled");
                $scope.recipients = [];
                loadSchedules();
            })
            .catch((error) => {
                const err = error.data;
                toaster.pop("error", (err && err.error) || "Error", "Failed to schedule report");
            })
            .finally(() => {
                $scope.saving = false;
            });
    };

    $scope.toggleActive = (scheduledReport) => {
        const type = scheduledReport.active ? "DEACTIVATE" : "ACTIVATE";
        reportService
            .toggleActivateScheduledSavedReport(scheduledReport.id, { type })
            .then(() => loadSchedules())
            .catch(() => toaster.pop("error", "Error", "Failed to update schedule"));
    };

    const loadSchedules = () => {
        return reportService
            .getAllScheduledWithHistoryByStandardReport($scope.standardReportId)
            .then((list) => {
                $scope.schedules = list.map((row) => row.scheduledReport);
            })
            .catch(() => toaster.pop("error", "Error", "Failed to load schedules"));
    };

    const parseFilterValueString = (filter) => {
        switch (filter.type) {
            case "TIMESTAMPTZ":
            case "TIMESTAMP":
            case "DATE":
                return $filter("mfShortTime")(filter.value, ["withDate", "withSeconds"]);
            case "TIME":
                return $filter("mfShortTime")(filter.value, ["withSeconds"]);
            case "MULTISELECT":
                return filter.value;
            default:
                return "" + filter.value;
        }
    };

    const parseFilterValueToObject = (filter) => {
        const valueString = parseFilterValueString(filter);

        if (filter.type === "MULTISELECT") {
            return { type: "multiselect", value: filter.value };
        }

        switch (filter.operator) {
            case "LAST X":
            case "THIS X":
            case "NEXT X":
                return { type: "timePeriod", timePeriod: valueString };
            case "LAST N X":
            case "NEXT N X":
                return {
                    type: "timePeriodInterval",
                    value: {
                        interval: filter.timePeriodValue.interval,
                        timePeriod: filter.timePeriodValue.timePeriod
                    }
                };
            case "LAST REL N X":
            case "NEXT REL N X":
                return {
                    type: "timePeriodNowRelative",
                    value: {
                        negate: false,
                        interval: filter.timePeriodValue.interval,
                        timePeriod: filter.timePeriodValue.timePeriod
                    }
                };
            case "NOW REL TIME COMPARE":
                return {
                    type: "timeComparisonNowRelative",
                    value: {
                        comparison: filter.timePeriodValue.comparison,
                        interval: filter.timePeriodValue.interval,
                        timePeriod: filter.timePeriodValue.timePeriod
                    }
                };
            default:
                return { type: "string", value: valueString };
        }
    };

    const parseFilters = (filters) => {
        for (let i = 0; i < filters.length; i++) {
            const item = filters[i];
            if (item["aggregate"] === undefined) {
                filters[i].aggregate = null;
            }
            if (item["$selectedModel"]) {
                item.value = [];
                for (let j = 0; j < item["$selectedModel"].length; j++) {
                    item.value.push(item["$selectedModel"][j].id);
                }
                if (item.value.length === 0) {
                    item.filterSelected = false;
                }
            }
        }

        return filters
            .filter((val) => val.filterSelected === true)
            .map((filter) => ({
                ...filter,
                tableColumn: filter.name,
                isLocked: filter.isLocked,
                value:
                    filter.$relative && filter.$relative.enabled
                        ? {
                              type: "relativeDate",
                              base: filter.$relative.base,
                              offsetUnit: filter.$relative.offsetUnit,
                              offsetAmount: Number(filter.$relative.offsetAmount) || 0
                          }
                        : parseFilterValueToObject(filter)
            }));
    };

    const parseSavedFilterValueToValue = (filter) => {
        if (filter.type === "NUMBER") {
            return Number.parseInt(filter.value.value, 10);
        }
        if (filter.type === "BOOLEAN") {
            return filter.value.value === "true";
        }
        switch (filter.operator) {
            case "LAST X":
            case "THIS X":
            case "NEXT X":
                return filter.value.timePeriod;
            case "LAST N X":
            case "NEXT N X":
            case "LAST REL N X":
            case "NEXT REL N X":
                return { interval: filter.value.interval, timePeriod: filter.value.timePeriod };
            case "NOW REL TIME COMPARE":
                return {
                    comparison: filter.value.comparison,
                    interval: filter.value.interval,
                    timePeriod: filter.value.timePeriod
                };
            default:
                return filter.value.value;
        }
    };

    const initialize = () => {
        reportService
            .getStandardReport($scope.standardReportId)
            .then((loaded) => {
                $scope.report = {
                    ...loaded.report_template,
                    reportType: loaded.report_type,
                    id: loaded.id,
                    name: loaded.name,
                    primaryTable:
                        loaded.report_type === "CUSTOM"
                            ? null
                            : loaded.report_template.primaryTable.tableName
                };
                for (const filter of $scope.report.filters) {
                    filter.value = parseSavedFilterValueToValue(filter);
                    if (isDateFilter(filter)) {
                        filter.$relative = {
                            enabled: false,
                            base: "START_OF_MONTH",
                            offsetUnit: "MONTH",
                            offsetAmount: 0
                        };
                    }
                }
            })
            .catch(() => toaster.pop("error", "Error", "Failed to load report"))
            .finally(() => {
                $scope.loading = false;
            });

        loadSchedules();
    };

    initialize();
}
